import * as React from "react";
import { CalendarCheck, CheckCircle2, Mail } from "lucide-react";

import { Poll, PollResults } from "@/api/types";
import { Button } from "@/components/ui/button";
import { conferenceProviderLabel } from "@/lib/calendarProviders";
import { cn } from "@/lib/utils";

/**
 * Confirming a time, and — separately — telling everyone about it.
 *
 * <p>Both are irreversible enough to deserve a look first, so each has its own inline review step
 * rather than firing on one click: confirming writes an event to the host's calendar, and sending
 * puts a meeting in the diary of everyone who answered. Nothing here is on a timer and nothing
 * happens automatically.</p>
 *
 * <p>A page-level card, not a modal: the results grid stays visible while the host decides, because
 * the grid is the reason they are deciding.</p>
 */

const WHEN = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});
const SENT = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });

/** A labelled row in the review list. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-xs font-bold uppercase tracking-wide text-foreground">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  );
}

export function PollConfirmPanel({
  poll,
  results,
  pendingOptionId,
  onCancelChoice,
  onConfirm,
  onUnconfirm,
  onSend,
  busy,
}: {
  poll: Poll;
  results: PollResults;
  /** A time the host clicked Choose on, awaiting review. Null when nothing is pending. */
  pendingOptionId: string | null;
  onCancelChoice: () => void;
  onConfirm: (optionId: string) => void;
  onUnconfirm: () => void;
  onSend: () => void;
  busy: boolean;
}) {
  const [reviewingSend, setReviewingSend] = React.useState(false);

  const optionFor = (id: string | null) =>
    id ? results.options.find((o) => o.option_id === id) : undefined;
  const pending = optionFor(pendingOptionId);
  const confirmed = optionFor(poll.confirmed_option_id);

  const where = [
    poll.conferencing_provider ? conferenceProviderLabel(poll.conferencing_provider) : null,
    poll.location,
  ]
    .filter(Boolean)
    .join(" · ");

  // Everyone who answered is invited, including the people who voted No on this time. They took
  // part in deciding, and the meeting is happening.
  const attendees = results.respondents;
  const noVoters = new Set(
    poll.confirmed_option_id || pendingOptionId
      ? attendees
          .filter((r) => r.votes[(poll.confirmed_option_id ?? pendingOptionId) as string] === "NO")
          .map((r) => r.id)
      : [],
  );

  // --- review a pending choice ---------------------------------------------
  if (pending) {
    return (
      <section className="space-y-3 rounded-xl border border-foreground bg-card p-4">
        <h3 className="text-sm font-medium text-foreground">Confirm this time?</h3>
        <div className="space-y-1.5">
          <Fact label="Time">{WHEN.format(new Date(pending.start_at))}</Fact>
          <Fact label="Duration">{poll.duration_minutes} minutes</Fact>
          {where && <Fact label="Where">{where}</Fact>}
          <Fact label="Confirmation email">Not sent yet</Fact>
        </div>
        <div className="space-y-1.5">
          <span className="text-xs font-bold uppercase tracking-wide text-foreground">
            Attendees ({attendees.length})
          </span>
          <div className="flex flex-wrap gap-1.5">
            {attendees.map((r) => (
              <span
                key={r.id}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm text-foreground",
                  noVoters.has(r.id) ? "border-brand-coral/50" : "border-border",
                )}
              >
                {r.name}
                {noVoters.has(r.id) && (
                  <span className="text-xs font-medium text-brand-coral">voted No</span>
                )}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => onConfirm(pending.option_id)} disabled={busy}>
            {busy ? "Confirming…" : "Confirm"}
          </Button>
          <Button variant="outline" onClick={onCancelChoice} disabled={busy}>
            Keep deciding
          </Button>
        </div>
      </section>
    );
  }

  // --- nothing confirmed yet ------------------------------------------------
  if (!confirmed) {
    return null;
  }

  // --- confirmed --------------------------------------------------------------
  return (
    <section className="space-y-4 rounded-xl border border-brand-teal/50 bg-brand-teal/10 p-4">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-teal" aria-hidden="true" />
        <div className="space-y-1.5">
          <h3 className="text-sm font-medium text-foreground">
            {WHEN.format(new Date(confirmed.start_at))} is confirmed
          </h3>
          <Fact label="Duration">{poll.duration_minutes} minutes</Fact>
          {where && <Fact label="Where">{where}</Fact>}
          {poll.meeting_url && (
            <Fact label="Join">
              <a href={poll.meeting_url} className="font-medium text-brand-blue underline-offset-2 hover:underline">
                {poll.meeting_url}
              </a>
            </Fact>
          )}
        </div>
      </div>

      <div className="space-y-2 border-t border-brand-teal/30 pt-3">
        {poll.confirmation_sent_at ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
              <Mail className="h-4 w-4 shrink-0 text-brand-teal" aria-hidden="true" />
              Sent to {attendees.length} {attendees.length === 1 ? "person" : "people"} on{" "}
              {SENT.format(new Date(poll.confirmation_sent_at))}
            </span>
            {reviewingSend ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <Button onClick={onSend} disabled={busy}>
                  {busy ? "Sending…" : `Send again to ${attendees.length}`}
                </Button>
                <Button variant="outline" onClick={() => setReviewingSend(false)} disabled={busy}>
                  Cancel
                </Button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setReviewingSend(true)}
                className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Send again
              </button>
            )}
          </div>
        ) : reviewingSend ? (
          <div className="space-y-2">
            <p className="text-sm text-foreground">
              This emails {attendees.length} {attendees.length === 1 ? "person" : "people"} with a
              calendar invitation for {WHEN.format(new Date(confirmed.start_at))}.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {attendees.map((r) => (
                <span
                  key={r.id}
                  className="rounded-full border border-border px-2 py-0.5 text-sm text-foreground"
                >
                  {r.email}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={onSend} disabled={busy}>
                {busy ? "Sending…" : "Send it"}
              </Button>
              <Button variant="outline" onClick={() => setReviewingSend(false)} disabled={busy}>
                Not yet
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => setReviewingSend(true)} disabled={busy}>
              <Mail className="mr-1.5 h-4 w-4" /> Send the confirmation email
            </Button>
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
              <CalendarCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
              Nobody has been told yet
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={onUnconfirm}
          disabled={busy}
          className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {poll.confirmation_sent_at
            ? "Withdraw this time and tell everyone it is off"
            : "Withdraw this time"}
        </button>
      </div>
    </section>
  );
}
